import React, { useEffect } from 'react';

import { Button } from '@qality/common-components/src/atoms/Button';
import { FieldWrapper } from '@qality/common-components/src/atoms/FieldWrapper';
import { Header } from '@qality/common-components/src/atoms/Header';
import { Icon } from '@qality/common-components/src/atoms/Icon';
import SectionMessage from '@qality/common-components/src/atoms/atlaskit/SectionMessage';
import { ProjectSelectError } from '@qality/common-components/src/molecules/ImportTestCasesForm/steps/UploadFiles/ProjectSelectError';
import { AsyncSelect } from '@qality/common-components/src/molecules/final-form/AsyncSelect';
import { FieldError } from '@qality/common-components/src/molecules/final-form/FieldError';
import { Select } from '@qality/common-components/src/molecules/final-form/Select';
import { ProjectSelectOption } from '@qality/common-model';
import { StorageOfUniqueServices } from '@qality/common-service';

import { TestCycleOption } from './SelectCycle';
import {
  SelectCycleMessageBoxStyled,
  SelectCycleStyled,
} from './SelectCycle.styled';

type Props = {
  noTestCases: boolean;
  testCycleOptions: Array<TestCycleOption>;
  isLoadingTestCycles: boolean;
  onCancel: () => void;
  loadOptions: (query: string) => Promise<Array<TestCycleOption>>;
  projectOptions: Array<ProjectSelectOption>;
  isTestCycleDropdownDisabled: boolean;
};

export const SelectCycleView = ({
  noTestCases,
  testCycleOptions,
  isLoadingTestCycles,
  onCancel,
  loadOptions,
  projectOptions,
  isTestCycleDropdownDisabled,
}: Props): JSX.Element => {
  const ICON_PATH =
    StorageOfUniqueServices.get('URLService').getQAlityIssueIcon();

  useEffect(() => {
    loadOptions('');
  }, []);

  const getPlaceholder = () => {
    if (isLoadingTestCycles) {
      return 'Loading..';
    }

    if (!isTestCycleDropdownDisabled && testCycleOptions.length === 0) {
      return 'Test Cycle list is empty';
    }

    return 'Search Test Cycles...';
  };

  return (
    <SelectCycleStyled>
      <Header spacing='medium'>Settings</Header>
      <p>Choose where selected Test Cases will be added</p>

      {noTestCases ? (
        <>
          <SelectCycleMessageBoxStyled>
            <SectionMessage title='No Test Cases Selected' appearance='error'>
              <span>
                Please make sure that your issue search results contain at least
                one Test Case (<Icon src={ICON_PATH} alt='Icon' />
                QAlity&nbsp;Test issue type) before proceeding.
              </span>
            </SectionMessage>
          </SelectCycleMessageBoxStyled>
          <Button onClick={onCancel} appearance='primary'>
            Back to searching issues
          </Button>
        </>
      ) : (
        <>
          <FieldWrapper size='large'>
            <Select
              name='project'
              label='Project'
              isDisabled={noTestCases}
              placeholder='Select project'
              isClearable
              isRequired
              options={projectOptions}
            />
            <ProjectSelectError />
          </FieldWrapper>
          <FieldWrapper size='large'>
            <AsyncSelect
              name='testCycle'
              label='Test Cycle'
              placeholder={getPlaceholder()}
              isClearable
              isRequired
              options={testCycleOptions}
              defaultOptions={testCycleOptions}
              loadOptions={loadOptions}
              isDisabled={isTestCycleDropdownDisabled}
            />
            <FieldError name='testCycle' />
          </FieldWrapper>
        </>
      )}
    </SelectCycleStyled>
  );
};
